import { Path } from '../types';
import { Trash2, Edit3, Sliders } from 'lucide-react';

interface PathPropertiesProps {
  path: Path;
  onUpdate: (updates: Partial<Path>) => void;
  onDelete: () => void;
}

export function PathProperties({ path, onUpdate, onDelete }: PathPropertiesProps) {
  const getPathTypeLabel = (type: Path['type']): string => {
    const labels: Record<Path['type'], string> = {
      course: 'Tracé de course',
      net: 'Filet',
      barrier: 'Barrière',
      'v-board': 'V-Board',
      sapinettes: 'Sapinettes',
      custom: 'Tracé personnalisé'
    };
    return labels[type];
  };

  const getDefaultColor = (type: Path['type']): string => {
    const colors: Record<Path['type'], string> = {
      course: '#3b82f6', // Bleu
      net: '#ef4444', // Rouge
      barrier: '#8b4513', // Marron (SaddleBrown)
      'v-board': '#ef4444', // Rouge
      sapinettes: '#ef4444', // Rouge
      custom: '#8b5cf6' // Violet
    };
    return colors[type];
  };

  const getDefaultStrokeWidth = (type: Path['type']): number => {
    const widths: Record<Path['type'], number> = {
      course: 1.5,
      net: 0.4,
      barrier: 0.4,
      'v-board': 0.4,
      sapinettes: 0.2,
      custom: 0.5
    };
    return widths[type];
  };

  const getDefaultDashArray = (type: Path['type']): string => {
    const dashArrays: Record<Path['type'], string> = {
      course: 'none',
      net: 'none',
      barrier: 'none',
      'v-board': '0.5,1',
      sapinettes: '0.05,1',
      custom: 'none'
    };
    return dashArrays[type];
  };

  const getDefaultSmooth = (type: Path['type']): boolean => {
    return type === 'course' || type === 'v-board' || type === 'net';
  };

  const presetDashPatterns = [
    { label: 'Continu', value: 'none' },
    { label: 'Points très fins (Sapinettes)', value: '0.05,1' },
    { label: 'Points rectangulaires (V-Board)', value: '0.5,1' },
    { label: 'Sapinettes ancienne version', value: '5,3' },
    { label: 'Pointillés courts', value: '5,5' },
    { label: 'Filet', value: '10,5' },
    { label: 'Barrière ancienne version', value: '15,10' },
    { label: 'Tirets longs', value: '20,10' },
    { label: 'Point-tiret', value: '15,5,5,5' }
  ];

  return (
    <div className="space-y-3">
      {/* Ligne 1 : Informations de base */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <div>
          <label className="block text-xs text-gray-700 mb-1">Type</label>
          <input
            type="text"
            value={getPathTypeLabel(path.type)}
            disabled
            className="w-full px-2 py-1.5 text-sm bg-gray-50 border border-gray-300 rounded"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-1">Libellé</label>
          <input
            type="text"
            value={path.label || ''}
            onChange={(e) => onUpdate({ label: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            placeholder="Optionnel"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-1">Couleur</label>
          <input
            type="color"
            value={path.color || getDefaultColor(path.type)}
            onChange={(e) => onUpdate({ color: e.target.value })}
            className="w-full h-[30px] border border-gray-300 rounded cursor-pointer"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-1">Épaisseur</label>
          <input
            type="number"
            min="0.05"
            max="5"
            step="0.05"
            value={path.strokeWidth || getDefaultStrokeWidth(path.type)}
            onChange={(e) => onUpdate({ strokeWidth: parseFloat(e.target.value) })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-1">Opacité (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            step="10"
            value={Math.round((path.opacity ?? (path.type === 'course' ? 0.5 : 1)) * 100)}
            onChange={(e) => onUpdate({ opacity: parseFloat(e.target.value) / 100 })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-end">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={path.smooth ?? getDefaultSmooth(path.type)}
              onChange={(e) => onUpdate({ smooth: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <span className="text-xs text-gray-700">Lisse</span>
          </label>
        </div>
      </div>

      {/* Ligne 2 : Style de trait */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-gray-700 mb-1">Style de trait</label>
          <select
            value={path.dashArray || getDefaultDashArray(path.type)}
            onChange={(e) => onUpdate({ dashArray: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
          >
            {presetDashPatterns.map((pattern) => (
              <option key={pattern.value} value={pattern.value}>
                {pattern.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-700 mb-1">Personnalisé (SVG)</label>
          <input
            type="text"
            value={path.dashArray || getDefaultDashArray(path.type)}
            onChange={(e) => onUpdate({ dashArray: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            placeholder="ex: 10,5"
          />
        </div>

        {(path.smooth ?? getDefaultSmooth(path.type)) && (
          <div>
            <label className="block text-xs text-gray-700 mb-1">Tension</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={path.tension || 0.5}
              onChange={(e) => onUpdate({ tension: parseFloat(e.target.value) })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      {/* Ligne 3 : Flèches (course uniquement) */}
      {path.type === 'course' && (
        <div className="border-t pt-2">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div>
              <label className="block text-xs text-gray-700 mb-1">Style flèche</label>
              <select
                value={path.arrowStyle || 'triangle'}
                onChange={(e) => onUpdate({ arrowStyle: e.target.value as 'triangle' | 'arrow' | 'chevron' })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              >
                <option value="triangle">Triangle</option>
                <option value="arrow">Flèche</option>
                <option value="chevron">Chevron</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-700 mb-1">Espacement</label>
              <input
                type="number"
                min="5"
                max="50"
                step="1"
                value={path.arrowSpacing || 15}
                onChange={(e) => onUpdate({ arrowSpacing: parseInt(e.target.value) })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-700 mb-1">Taille (px)</label>
              <input
                type="number"
                min="50"
                max="500"
                step="10"
                value={path.arrowSize || 100}
                onChange={(e) => onUpdate({ arrowSize: parseInt(e.target.value) })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-700 mb-1">Opacité (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="10"
                value={Math.round((path.arrowOpacity ?? 1) * 100)}
                onChange={(e) => onUpdate({ arrowOpacity: parseFloat(e.target.value) / 100 })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-700 mb-1">Épaisseur</label>
              <input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                value={path.arrowStrokeWidth || 2}
                onChange={(e) => onUpdate({ arrowStrokeWidth: parseFloat(e.target.value) })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="border-t pt-2 flex items-center justify-between">
        <span className="text-xs text-gray-500">{path.points.length} point(s)</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onUpdate({ 
              color: undefined, 
              strokeWidth: undefined, 
              opacity: undefined,
              dashArray: undefined,
              arrowSize: undefined,
              arrowStyle: undefined,
              arrowOpacity: undefined,
              arrowStrokeWidth: undefined,
              smooth: undefined,
              tension: undefined
            })}
            className="text-xs px-3 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
          >
            Réinitialiser
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-xs px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}